import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface ChatConversation {
  id: string;
  user_id: string;
  subject: string;
  status: 'open' | 'closed';
  last_message_at: string;
  created_at: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
  unread_count?: number;
}

export interface ChatMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  read_at: string | null;
  created_at: string;
  sender?: {
    id: string;
    name: string;
    email: string;
  };
}

export function useChat() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const isAdmin = user?.role === 'admin';

  // Fetch conversations
  const { data: conversations = [], isLoading: conversationsLoading, refetch: refetchConversations } = useQuery({
    queryKey: ['chat-conversations', user?.id, isAdmin],
    queryFn: async () => {
      let query = supabase
        .from('chat_conversations')
        .select(`
          *,
          user:users!chat_conversations_user_id_fkey(id, name, email)
        `)
        .order('last_message_at', { ascending: false });

      if (!isAdmin) {
        query = query.eq('user_id', user?.id);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Get unread counts for each conversation
      const conversationsWithUnread = await Promise.all(
        (data || []).map(async (conv) => {
          const { count } = await supabase
            .from('chat_messages')
            .select('*', { count: 'exact', head: true })
            .eq('conversation_id', conv.id)
            .is('read_at', null)
            .neq('sender_id', user?.id);

          return {
            ...conv,
            unread_count: count || 0
          };
        })
      );

      return conversationsWithUnread as ChatConversation[];
    },
    enabled: !!user?.id,
    refetchInterval: 30000
  });

  // Fetch messages for active conversation
  const { data: messages = [], isLoading: messagesLoading, refetch: refetchMessages } = useQuery({
    queryKey: ['chat-messages', activeConversationId],
    queryFn: async () => {
      if (!activeConversationId) return [];
      
      const { data, error } = await supabase
        .from('chat_messages')
        .select(`
          *,
          sender:users!chat_messages_sender_id_fkey(id, name, email)
        `)
        .eq('conversation_id', activeConversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data as ChatMessage[];
    },
    enabled: !!activeConversationId
  });

  // Realtime subscription for messages
  useEffect(() => {
    if (!activeConversationId) return;

    const channel = supabase
      .channel(`chat-messages-${activeConversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `conversation_id=eq.${activeConversationId}`
        },
        () => {
          refetchMessages();
          refetchConversations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeConversationId, refetchMessages, refetchConversations]);

  // Realtime subscription for new conversations (admin)
  useEffect(() => {
    if (!isAdmin) return;

    const channel = supabase
      .channel('chat-conversations-admin')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_conversations'
        },
        () => {
          refetchConversations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAdmin, refetchConversations]);

  // Create conversation mutation
  // userId is optional - if provided (admin), creates conversation for that user
  // if not provided, creates for current user
  const createConversation = useMutation({
    mutationFn: async ({ subject, initialMessage, userId }: { 
      subject: string; 
      initialMessage: string;
      userId?: string;
    }) => {
      // Determine target user - if admin specifies userId, use that, otherwise use current user
      const targetUserId = userId || user?.id;
      
      // Create conversation
      const { data: conv, error: convError } = await supabase
        .from('chat_conversations')
        .insert({
          user_id: targetUserId,
          subject
        })
        .select()
        .single();

      if (convError) throw convError;

      // Create initial message (always from current user/admin)
      const { error: msgError } = await supabase
        .from('chat_messages')
        .insert({
          conversation_id: conv.id,
          sender_id: user?.id,
          content: initialMessage
        });

      if (msgError) throw msgError;

      return conv;
    },
    onSuccess: (conv) => {
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
      setActiveConversationId(conv.id);
      toast.success('Conversation créée');
    },
    onError: () => {
      toast.error('Erreur lors de la création de la conversation');
    }
  });

  // Send message mutation
  const sendMessage = useMutation({
    mutationFn: async ({ conversationId, content }: { conversationId: string; content: string }) => {
      const { error } = await supabase
        .from('chat_messages')
        .insert({
          conversation_id: conversationId,
          sender_id: user?.id,
          content
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-messages'] });
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
    },
    onError: () => {
      toast.error('Erreur lors de l\'envoi du message');
    }
  });

  // Mark messages as read
  const markAsRead = useMutation({
    mutationFn: async (conversationId: string) => {
      const { error } = await supabase
        .from('chat_messages')
        .update({ read_at: new Date().toISOString() })
        .eq('conversation_id', conversationId)
        .is('read_at', null)
        .neq('sender_id', user?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
    }
  });

  // Close conversation (admin only)
  const closeConversation = useMutation({
    mutationFn: async (conversationId: string) => {
      const { error } = await supabase
        .from('chat_conversations')
        .update({ status: 'closed' })
        .eq('id', conversationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
      toast.success('Conversation fermée');
    }
  });

  // Reopen conversation
  const reopenConversation = useMutation({
    mutationFn: async (conversationId: string) => {
      const { error } = await supabase
        .from('chat_conversations')
        .update({ status: 'open' })
        .eq('id', conversationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
      toast.success('Conversation réouverte');
    }
  });

  // Calculate total unread
  const totalUnread = conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);

  return {
    conversations,
    messages,
    conversationsLoading,
    messagesLoading,
    activeConversationId,
    setActiveConversationId,
    createConversation,
    sendMessage,
    markAsRead,
    closeConversation,
    reopenConversation,
    totalUnread,
    isAdmin
  };
}
